/* eslint-disable max-lines */

import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH,
  SHIFT_DETAILS_TIMEKEEPING_FULL_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { isBackgroundCheckError } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/handleBackgroundCheckError";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { type NfcTagRequest } from "@src/appV2/Shifts/Shift/types";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  type TimekeepingData,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { NfcHashValidationAction } from "../NfcValidation/api/types";
import { useDisableShiftNfcCheck } from "../NfcValidation/api/useDisableShiftNfcCheck";
import { WorkerNfcValidation } from "../NfcValidation/WorkerNfcValidation";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { DisableShiftNfcFailed } from "./DisableShiftNfcFailed";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { getClockInSuccessDescription } from "./utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "./utils/getHasWorkerClockedInOnTime";
import { WorkplaceRulesReminderContent } from "./WorkplaceRulesReminderContent";

export interface ClockInBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  shiftStartDate: Date;
  earliestGeofenceEntryTime?: Date;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  isNfcClockInEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  breakPolicyNoteId: string;
  requiresBreakPolicyAcknowledgement: boolean;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isSecondShiftInDouble: boolean;
  targetArrivalTime?: Date;
  timezone?: string;
  shiftBookedAt?: Date;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  workerStage: WorkerStage;
  workerSubstage?: string;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  isMissedPunchRequestEnabled: boolean;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
  onTimekeepingActionFailed: (props: { error: string }) => void;
}

type ClockInBottomSheetStep =
  | "nfc-validation"
  | "requesting-clock-in"
  | "requesting-non-instant-pay"
  | "disable-shift-nfc-check-failed"
  | "clock-in-success"
  | "workplace-rules";

export function ClockInBottomSheet(props: ClockInBottomSheetProps) {
  const {
    modalState,
    shiftId,
    shiftStartDate,
    earliestGeofenceEntryTime,
    workerId,
    workplaceId,
    workplaceName,
    isNfcClockInEnabled: isNfcClockInEnabledProperty,
    nfcTagRequests,
    breakPolicyNoteId,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isSecondShiftInDouble,
    targetArrivalTime,
    timezone,
    shiftBookedAt,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    workerStage,
    workerSubstage,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
    isMissedPunchRequestEnabled,
    onTimekeepingActionCompleted,
    onTimekeepingActionFailed,
  } = props;

  const history = useHistory();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const [isNfcClockInEnabled, setIsNfcClockInEnabled] = useState(isNfcClockInEnabledProperty);

  const initialStep = isNfcClockInEnabled ? "nfc-validation" : "requesting-clock-in";

  const [step, setStep] = useState<ClockInBottomSheetStep>(initialStep);

  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();
  // Manual refs to track mutation state
  const isClockInPendingRef = useRef(false);
  const hasClockInSucceededRef = useRef(false);

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();
  const { mutateAsync: callDisableShiftNfcCheck } = useDisableShiftNfcCheck();

  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { wait, skip } = useSkippableWait();

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const clockInTime = postClockInShift?.clockInOut?.start
    ? parseISO(postClockInShift.clockInOut.start)
    : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate,
    clockInTime,
  });

  const abortFlowAndClose = useCallback(() => {
    skip();
    modalState.closeModal();
    setStep(initialStep);
  }, [modalState, skip, initialStep]);

  const completeSuccessFlow = useCallback(
    async (timekeepingData?: TimekeepingData) => {
      setStep("clock-in-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);

      // Manually set shift as clocked in before the API GET call is made
      if (timekeepingData) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: timekeepingData.clockInOut,
                },
              }
            : undefined
        );
      }

      history.replace(
        generatePath(SHIFT_DETAILS_TIMEKEEPING_FULL_PATH, {
          shiftId,
        }),
        { justClockedIn: true }
      );
    },
    [wait, queryClient, shiftId, history]
  );

  const onClockInSuccess = useCallback(
    async (timekeepingData: TimekeepingData) => {
      onTimekeepingActionCompleted();

      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else {
        await completeSuccessFlow(timekeepingData);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      onTimekeepingActionCompleted,
    ]
  );

  const recordClockIn = useCallback(async () => {
    // Check manual refs to prevent double execution. Without these, double clock in is possible
    // if user calls disableShiftNfcCheck(), which changes state variables and triggers the below
    // useEffect. Checking .isLoading and.isSuccess on the mutation don't work, likely because
    // that state gets reset due to re-render of this component.
    if (isClockInPendingRef.current || hasClockInSucceededRef.current) {
      return;
    }

    // Mark as pending
    isClockInPendingRef.current = true;

    try {
      const timekeepingData = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_IN,
        shiftActionCheck: isNfcClockInEnabled ? ActionCheckType.NFC : ActionCheckType.LOCATION,
        verificationMethod: isNfcClockInEnabled
          ? AttendanceVerificationMethod.NFC_TAG
          : AttendanceVerificationMethod.GEOFENCE,
      });

      // Mark as succeeded
      hasClockInSucceededRef.current = true;
      await onClockInSuccess(timekeepingData);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      onTimekeepingActionFailed({ error: errorMessage });

      if (isBackgroundCheckError(error)) {
        modalState.closeModal();
        history.replace(MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH);
        return;
      }

      showErrorToast(errorMessage);

      abortFlowAndClose();
    } finally {
      // Clear pending state
      isClockInPendingRef.current = false;
    }
  }, [
    recordTimekeepingAction,
    shiftId,
    isNfcClockInEnabled,
    onClockInSuccess,
    onTimekeepingActionFailed,
    showErrorToast,
    abortFlowAndClose,
    history,
    modalState,
  ]);

  // Recording clock in is usually a result of a user action somewhere in this sheet,
  // but in this specific case (the sheet has just opened and NFC is not enabled). In the future
  // it would be better to trigger this code as a result of explicit user interaction (e.g. button click)
  useEffect(() => {
    if (modalState.modalIsOpen && step === "requesting-clock-in" && !isNfcClockInEnabled) {
      void recordClockIn();
    }
  }, [modalState.modalIsOpen, step, isNfcClockInEnabled, recordClockIn]);

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: isNfcClockInEnabled
          ? ShiftMarkedNonIpReasons.NFC_FAILURE
          : ShiftMarkedNonIpReasons.NO_LOCATION,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: isNfcClockInEnabled ? "NFC_FAILURE" : "NO_LOCATION",
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  async function disableShiftNfcCheck() {
    setStep("requesting-clock-in");

    try {
      await callDisableShiftNfcCheck({ shiftId, workerId });
      setIsNfcClockInEnabled(false);
    } catch {
      setStep("disable-shift-nfc-check-failed");
      return;
    }

    await recordClockIn();
  }

  return (
    <>
      {requiresBreakPolicyAcknowledgement && (
        <BreakConfirmationBottomSheet
          action={NoteAcknowledgementAction.CLOCK_IN}
          isLoading={false}
          modalState={modalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          breakPolicyNoteId={breakPolicyNoteId}
          onAcknowledge={async () => {
            setRequiresBreakPolicyAcknowledgement(false);
          }}
        />
      )}

      {!requiresBreakPolicyAcknowledgement && (
        <BottomSheet modalState={modalState} canDismiss={false}>
          {step === "nfc-validation" && (
            <WorkerNfcValidation
              shiftId={shiftId}
              metaProps={{ workerId, workplaceId, workplaceName }}
              nfcTagRequests={nfcTagRequests}
              clockAction={NfcHashValidationAction.CLOCK_IN}
              isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
              onSuccess={async () => {
                setStep("requesting-clock-in");
                await recordClockIn();
              }}
              onSkipLocationCheck={async () => {
                setStep("requesting-non-instant-pay");
                await convertToNonInstantPay();
              }}
              onCancel={() => {
                abortFlowAndClose();
              }}
              onNfcNotSupported={async () => {
                await disableShiftNfcCheck();
              }}
            />
          )}
          {step === "requesting-clock-in" && (
            <BottomSheetLoadingContent
              title="Confirming your clock in"
              description="Should only take a few seconds"
            />
          )}
          {step === "clock-in-success" && (
            <TimekeepingActionSuccessContent
              title="Have a great shift!"
              description={getClockInSuccessDescription({
                hasWorkerClockedInOnTime,
                isSecondShiftInDouble,
                earliestGeofenceEntryTime,
                targetArrivalTime,
                shiftStartDate,
                clockInTime,
                timezone,
                shiftBookedAt,
                isTimesheetEditDisabled,
                shouldEnforceNfcTimesheetBounds,
                isArrivalTimeFeatureEnabled,
                policy: clipboardScoreProfile?.policy,
                workerStage,
                workerSubstage,
                isClipboardScoreFreezeEnabled,
                clipboardScoreFreezeReason,
              })}
              onClose={() => {
                skip();
                modalState.closeModal();
                // Note that we don't call abortFlowAndClose here because it resets the step to geofence-check
                // which may call the geofence check logic again before redirecting to the post-clock in flow.
              }}
            />
          )}
          {step === "workplace-rules" && workplaceRules && (
            <WorkplaceRulesReminderContent
              workplaceRules={workplaceRules}
              shiftId={shiftId}
              workplaceId={workplaceId}
              onContinue={async () => {
                await completeSuccessFlow(postClockInShift);
              }}
            />
          )}
          {step === "requesting-non-instant-pay" && (
            <BottomSheetLoadingContent
              title="Converting to non-instant pay"
              description="Should only take a few seconds"
            />
          )}
          {step === "disable-shift-nfc-check-failed" && (
            <DisableShiftNfcFailed
              onTryAgain={async () => {
                await disableShiftNfcCheck();
              }}
              onClose={() => {
                abortFlowAndClose();
              }}
            />
          )}
        </BottomSheet>
      )}
    </>
  );
}
/* eslint-enable max-lines */
